/**
 * TicketChat — shared chat component for /support/:id and /admin/tickets/:id
 *
 * Features:
 * - Real-time messages via onSnapshot
 * - Composer with textarea + image attachment (click, drag & drop, or Ctrl/Cmd+V paste)
 * - Up to 5 images per message, max 5 MB each, JPEG/PNG/WebP/GIF
 * - Image previews before sending; lightbox on click
 * - Disabled when ticket is closed (client-side) — server also enforces it
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getDownloadURL, ref } from 'firebase/storage';
import { ImagePlus, Paperclip, Send, X, ZoomIn } from 'lucide-react';
import { storage } from '@/lib/firebase';
import { callAddTicketMessage, type AttachmentMeta } from '@/lib/functions';
import { uploadTicketAttachment } from '@/lib/storage';
import { watchTicketMessages } from '@/lib/firestore';
import type { TicketMessage, TicketAttachment, TicketStatus } from '@/types/models';
import { useT } from '@/lib/i18n';
import type { Locale } from '@/lib/i18n';
import { cn, fmtDate, type TranslateFn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

interface AttachmentDraft {
  file: File;
  preview: string; // object URL
}

interface TicketChatProps {
  ticketId: string;
  status: TicketStatus;
  /** Called after a message is successfully sent (e.g. to refresh ticket) */
  onMessageSent?: () => void;
}

export function TicketChat({ ticketId, status, onMessageSent }: TicketChatProps) {
  const { t, locale } = useT();
  const { user } = useAuth();

  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [body, setBody] = useState('');
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);
  const [attachError, setAttachError] = useState('');
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const isClosed = status === 'closed';

  // Real-time messages
  useEffect(() => {
    return watchTicketMessages(ticketId, setMessages);
  }, [ticketId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      drafts.forEach(d => URL.revokeObjectURL(d.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback((files: File[]) => {
    setAttachError('');
    const next = [...drafts];
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setAttachError(t('support.attach.error.type'));
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        setAttachError(t('support.attach.error.size'));
        return;
      }
      if (next.length >= MAX_ATTACHMENTS) {
        setAttachError(t('support.attach.error.count'));
        return;
      }
      next.push({ file, preview: URL.createObjectURL(file) });
    }
    setDrafts(next);
  }, [drafts, t]);

  // Paste screenshot support
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (isClosed) return;
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItems = items.filter(i => i.kind === 'file' && i.type.startsWith('image/'));
      if (imageItems.length === 0) return;
      e.preventDefault();
      const files = imageItems.map(i => i.getAsFile()!).filter(Boolean);
      addFiles(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isClosed, addFiles]);

  const removeDraft = (idx: number) => {
    setDrafts(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (isClosed || sending) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragging(false);
    if (isClosed || sending || drafts.length >= MAX_ATTACHMENTS) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) addFiles(files);
  };

  const handleSend = async () => {
    if (sending || isClosed) return;
    if (!body.trim() && drafts.length === 0) return;

    setSending(true);
    try {
      const attachmentMeta: AttachmentMeta[] = drafts.map(d => ({
        contentType: d.file.type,
        sizeBytes: d.file.size,
        fileName: d.file.name,
      }));

      const resp = await callAddTicketMessage({
        ticketId,
        body,
        attachmentMeta,
      });

      const uploads = resp.data.uploads ?? [];

      // Upload images in parallel
      if (uploads.length > 0) {
        await Promise.allSettled(
          drafts.map((d, i) => {
            const slot = uploads[i];
            if (!slot) return Promise.resolve();
            return uploadTicketAttachment(slot.path, d.file).promise;
          }),
        );
      }

      // Cleanup
      drafts.forEach(d => URL.revokeObjectURL(d.preview));
      setBody('');
      setDrafts([]);
      setAttachError('');
      onMessageSent?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error';
      setAttachError(msg);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="relative flex flex-col gap-4"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dragging && !isClosed && (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-500 bg-brand-50/95 dark:border-primary dark:bg-primary/15">
          <ImagePlus className="h-8 w-8 text-brand-600 dark:text-primary" />
          <span className="text-sm font-medium text-brand-700 dark:text-primary">
            {t('support.attach.drop')}
          </span>
        </div>
      )}

      {/* Messages */}
      <div className="flex min-h-[300px] max-h-[600px] flex-col gap-3 overflow-y-auto rounded-xl border border-ink-200 bg-slate-50 p-4 dark:border-border dark:bg-ink-900/40">
        {messages.length === 0 && (
          <p className="m-auto text-sm text-ink-400 dark:text-muted-foreground">—</p>
        )}
        {messages.map(msg => (
          <ChatBubble
            key={msg.id}
            msg={msg}
            isMine={msg.authorUid === user?.uid}
            locale={locale}
            t={t}
            onImageClick={setLightbox}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Attachment previews */}
      {drafts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {drafts.map((d, i) => (
            <div key={i} className="relative">
              <img
                src={d.preview}
                alt={d.file.name}
                className="h-16 w-16 rounded-lg border border-ink-200 object-cover dark:border-border"
              />
              <button
                onClick={() => removeDraft(i)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink-900 text-white hover:bg-red-600"
                title={t('support.attach.remove')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {attachError && (
        <p className="text-sm text-red-600 dark:text-red-400">{attachError}</p>
      )}

      {/* Composer */}
      {isClosed ? (
        <p className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-sm text-ink-500 dark:border-border dark:bg-secondary dark:text-muted-foreground">
          {t('support.chat.closed')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('support.chat.placeholder')}
              rows={3}
              disabled={sending}
              className="flex-1 resize-none rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground dark:placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={sending || drafts.length >= MAX_ATTACHMENTS}
                onClick={() => fileInputRef.current?.click()}
                title={t('support.chat.attach')}
                className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-xs text-ink-500 hover:bg-ink-50 disabled:opacity-40 dark:border-border dark:text-muted-foreground dark:hover:bg-secondary"
              >
                <Paperclip className="h-3.5 w-3.5" />
                {t('support.chat.attach')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={e => {
                  const files = Array.from(e.target.files ?? []);
                  addFiles(files);
                  e.target.value = '';
                }}
              />
              <span className="text-xs text-ink-400 dark:text-muted-foreground">
                {drafts.length}/{MAX_ATTACHMENTS} · {t('support.attach.hint')}
              </span>
            </div>
            <button
              onClick={handleSend}
              disabled={sending || (!body.trim() && drafts.length === 0)}
              className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-40 dark:bg-primary dark:hover:bg-primary/80"
            >
              <Send className="h-3.5 w-3.5" />
              {sending ? t('support.chat.sending') : t('support.chat.send')}
            </button>
          </div>
        </div>
      )}

      {/* Lightbox — portaled to body so it sits above Header stacking contexts */}
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >
          <img
            src={lightbox}
            alt="attachment"
            className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-6 top-6 z-[101] rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
            aria-label={t('common.close')}
          >
            <X className="h-7 w-7" />
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── ChatBubble ──────────────────────────────────────────────────────────────

interface ChatBubbleProps {
  msg: TicketMessage;
  isMine: boolean;
  locale: Locale;
  t: TranslateFn;
  onImageClick: (url: string) => void;
}

function ChatBubble({ msg, isMine, locale, t, onImageClick }: ChatBubbleProps) {
  const isAdmin = msg.authorRole === 'admin';
  const align = isMine ? 'items-end' : 'items-start';

  return (
    <div className={cn('flex flex-col gap-1', align)}>
      {/* Author + time */}
      <div className="flex items-center gap-1.5 text-[0.7rem] text-ink-500 dark:text-muted-foreground">
        <span className="font-medium text-ink-700 dark:text-foreground/80">
          {isAdmin ? t('support.chat.support_team') : msg.authorName || msg.authorEmail}
        </span>
        <span>·</span>
        <span>{fmtDate(msg.createdAt, locale)}</span>
      </div>

      {/* Bubble — color by role (user vs support), not by "mine" */}
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm text-white shadow-sm',
          isMine ? 'rounded-br-sm' : 'rounded-bl-sm',
          isAdmin
            ? 'bg-slate-800 dark:bg-slate-700'
            : 'bg-brand-800 dark:bg-brand-900',
        )}
      >
        {msg.body && <p className="whitespace-pre-wrap break-words">{msg.body}</p>}

        {/* Attachments */}
        {msg.attachments?.length > 0 && (
          <div className={cn('flex flex-wrap gap-2', msg.body ? 'mt-2' : '')}>
            {msg.attachments.map(att => (
              <StorageImage
                key={att.id}
                attachment={att}
                unavailableLabel={t('support.chat.image_unavailable')}
                onImageClick={onImageClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── StorageImage — resolves Storage URL lazily ──────────────────────────────

function StorageImage({
  attachment,
  unavailableLabel,
  onImageClick,
}: {
  attachment: TicketAttachment;
  unavailableLabel: string;
  onImageClick: (url: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const r = ref(storage, attachment.storagePath);
    getDownloadURL(r)
      .then(setUrl)
      .catch(() => setFailed(true));
  }, [attachment.storagePath]);

  if (failed) {
    return (
      <span className="rounded bg-black/20 px-2 py-1 text-xs opacity-70">
        {unavailableLabel}
      </span>
    );
  }

  if (!url) {
    return (
      <div className="h-16 w-16 animate-pulse rounded-lg bg-black/20" />
    );
  }

  return (
    <button
      type="button"
      onClick={() => onImageClick(url)}
      className="group relative h-16 w-16 overflow-hidden rounded-lg"
      title={attachment.fileName}
    >
      <img src={url} alt={attachment.fileName} className="h-full w-full object-cover" />
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/30">
        <ZoomIn className="h-5 w-5 text-white opacity-0 transition group-hover:opacity-100" />
      </div>
    </button>
  );
}
